import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme';

function AddProductModal({ isOpen, onClose, onAdd, categories, sizes = [] }) {
  // Helper function to sort sizes in proper order
  const sortSizes = (sizesToSort) => {
    if (!sizesToSort || sizesToSort.length === 0) return sizesToSort;
    
    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
    const slippersSizes = ['5', '6', '7', '8', '9', '10', '11', '12', '13'];
    const sorted = [...sizesToSort].sort((a, b) => {
      const aName = a.name.trim();
      const bName = b.name.trim();
      const aNameUpper = aName.toUpperCase();
      const bNameUpper = bName.toUpperCase();
      
      // Check if both are numeric slippers sizes
      const aIsNumeric = slippersSizes.includes(aName);
      const bIsNumeric = slippersSizes.includes(bName);
      
      if (aIsNumeric && bIsNumeric) {
        return parseInt(aName) - parseInt(bName);
      }
      
      const aIndex = sizeOrder.indexOf(aNameUpper);
      const bIndex = sizeOrder.indexOf(bNameUpper);
      
      // Standard sizes come first, then inches, then numeric sizes
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      if (aIsNumeric && !bIsNumeric) return 1;
      if (!aIsNumeric && bIsNumeric) return -1;
      return aNameUpper.localeCompare(bNameUpper);
    });
    return sorted;
  };
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discount: '',
    sellingPrice: '',
    purchasePrice: '',
    category: categories && categories.length > 0 ? categories[0].name : '',
    image: null,
    imagePreview: null,
    sizesData: {},
    sizesPricingData: {}, // For per-size pricing in Tapes: { sizeId: { price, purchasePrice, discount, sellingPrice } }
    offers: [], // { minQty, discountPercent, label }
    gst: '5',
    lowStockThreshold: '10',
    hasRightLeft: false,
    hasNoSizeSelection: false,
    hsn: '',
    usesPerSizePricing: false,
  });

  // Effective per-size pricing: category wins, then product-level flag
  const getCategoryUsesPerSizePricing = () => {
    const selectedCategory = categories.find(cat => cat.name === formData.category);
    return selectedCategory?.usesPerSizePricing === true || formData.category?.toLowerCase() === 'tapes';
  };

  const effectiveUsesPerSizePricing = () => {
    return getCategoryUsesPerSizePricing() || formData.usesPerSizePricing;
  };



  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'category') {
      // Get the selected category object
      const selectedCategory = categories.find(cat => cat.name === value);
      const categoryUsesPerSize = selectedCategory?.usesPerSizePricing === true || value?.toLowerCase() === 'tapes';
      
      // Do NOT auto-select sizes; leave all empty for the user to choose
      const sizeMap = {};
      const pricingMap = {};
      
      setFormData({
        ...formData,
        [name]: value,
        sizesData: sizeMap,
        sizesPricingData: (categoryUsesPerSize || formData.usesPerSizePricing) ? pricingMap : {},
        // Reset product-level flag if category forces per-size pricing
        usesPerSizePricing: categoryUsesPerSize ? false : formData.usesPerSizePricing,
      });
    } else if (name === 'price' || name === 'discount') {
      // When MRP or discount changes, recalculate selling price
      const newFormData = { ...formData, [name]: value };
      if (newFormData.price && newFormData.discount) {
        const calculated = Math.round(parseFloat(newFormData.price) - (parseFloat(newFormData.price) * parseFloat(newFormData.discount) / 100));
        newFormData.sellingPrice = calculated.toString();
      }
      setFormData(newFormData);
    } else if (name === 'sellingPrice') {
      // When selling price changes, calculate discount
      const newFormData = { ...formData, [name]: value };
      if (newFormData.price && value) {
        const sellingValue = parseFloat(value);
        const mrp = parseFloat(newFormData.price);
        if (sellingValue < mrp) {
          const discountAmount = mrp - sellingValue;
          const discountPercent = (discountAmount / mrp) * 100;
          newFormData.discount = discountPercent.toFixed(2);
        }
      }
      setFormData(newFormData);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSizeQuantityChange = (sizeId, quantity) => {
    setFormData({
      ...formData,
      sizesData: {
        ...formData.sizesData,
        [sizeId]: quantity ? parseInt(quantity) : 0,
      },
    });
  };

  const handleSizePricingChange = (sizeId, field, value) => {
    setFormData((prev) => {
      const current = prev.sizesPricingData[sizeId] || { price: '', purchasePrice: '', discount: '', sellingPrice: '' };
      const updated = { ...current, [field]: value ? parseFloat(value) : '' };

      const mrp = parseFloat(updated.price) || 0;
      const selling = parseFloat(updated.sellingPrice) || 0;
      const discount = updated.discount !== '' ? parseFloat(updated.discount) : '';

      if (field === 'price' || field === 'discount') {
        if (mrp > 0 && discount !== '') {
          updated.sellingPrice = Math.round(mrp * (1 - discount / 100));
        }
      } else if (field === 'sellingPrice') {
        if (mrp > 0 && selling > 0 && selling <= mrp) {
          updated.discount = parseFloat(((mrp - selling) / mrp * 100).toFixed(2));
        } else if (selling > mrp) {
          updated.discount = 0;
        }
      }

      return {
        ...prev,
        sizesPricingData: {
          ...prev.sizesPricingData,
          [sizeId]: updated,
        },
      };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('Image loaded, setting preview:', reader.result.substring(0, 50) + '...');
        setFormData((prev) => ({
          ...prev,
          image: reader.result, // Store the base64 string directly
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const getOfferReferenceMrp = (offer) => {
    const selectedCategory = categories.find(cat => cat.name === formData.category);
    const categoryUsesPerSize = selectedCategory?.usesPerSizePricing === true || formData.category?.toLowerCase() === 'tapes';
    const usesPerSizePricing = categoryUsesPerSize || formData.usesPerSizePricing;

    if (!usesPerSizePricing) {
      return parseFloat(formData.price) || 0;
    }

    // Per-size product: use selected size's price if offer is size-specific
    const sizeId = offer?.sizeId;
    if (sizeId && sizeId !== 'all' && sizeId !== '') {
      const pricing = formData.sizesPricingData[sizeId];
      if (pricing && parseFloat(pricing.price) > 0) {
        return parseFloat(pricing.price);
      }
    }
    return 0;
  };

  const handleOfferChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedOffers = [...(prev.offers || [])];
      const offer = { ...updatedOffers[index], [field]: value === '' ? '' : (field === 'sizeId' ? value : parseFloat(value)) };

      // Recalculate rate/discount when one changes
      if (field === 'discountPercent') {
        const mrp = getOfferReferenceMrp(offer);
        if (mrp > 0 && value !== '') {
          const discount = parseFloat(value) || 0;
          offer.rate = Math.round(mrp * (1 - discount / 100));
        } else if (value === '') {
          offer.rate = '';
        }
      } else if (field === 'rate') {
        const mrp = getOfferReferenceMrp(offer);
        if (mrp > 0 && value !== '') {
          const rate = parseFloat(value) || 0;
          offer.discountPercent = parseFloat((((mrp - rate) / mrp) * 100).toFixed(2));
        } else if (value === '') {
          offer.discountPercent = '';
        }
      } else if (field === 'sizeId') {
        // When size changes, recompute rate from existing discount if possible
        const mrp = getOfferReferenceMrp(offer);
        if (mrp > 0 && offer.discountPercent !== '' && offer.discountPercent !== undefined) {
          const discount = parseFloat(offer.discountPercent) || 0;
          offer.rate = Math.round(mrp * (1 - discount / 100));
        } else if (mrp > 0 && offer.rate !== '' && offer.rate !== undefined) {
          const rate = parseFloat(offer.rate) || 0;
          offer.discountPercent = parseFloat((((mrp - rate) / mrp) * 100).toFixed(2));
        } else {
          offer.rate = '';
          offer.discountPercent = '';
        }
      }

      updatedOffers[index] = offer;
      return { ...prev, offers: updatedOffers };
    });
  };

  const handleOfferLabelChange = (index, value) => {
    setFormData((prev) => {
      const updatedOffers = [...(prev.offers || [])];
      updatedOffers[index] = { ...updatedOffers[index], label: value };
      return { ...prev, offers: updatedOffers };
    });
  };

  const addOffer = () => {
    setFormData((prev) => ({
      ...prev,
      offers: [...(prev.offers || []), { minQty: '', discountPercent: '', rate: '', label: '', sizeId: 'all' }],
    }));
  };

  const removeOffer = (index) => {
    setFormData((prev) => ({
      ...prev,
      offers: (prev.offers || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Get the selected category object to check if it uses per-size pricing
    const selectedCategory = categories.find(cat => cat.name === formData.category);
    const categoryUsesPerSizePricing = selectedCategory?.usesPerSizePricing === true || formData.category?.toLowerCase() === 'tapes';
    const usesPerSizePricing = categoryUsesPerSizePricing || formData.usesPerSizePricing;
    
    // Validation: name is always required
    if (!formData.name.trim()) {
      alert('Product name is required');
      return;
    }

    // Validation for per-size pricing (skip for no-size products)
    if (usesPerSizePricing && !formData.hasNoSizeSelection) {
      // Check that at least one size is selected with quantity > 0
      const selectedSizeIds = Object.entries(formData.sizesData)
        .filter(([_, qty]) => parseInt(qty) > 0)
        .map(([sizeId]) => sizeId);

      if (selectedSizeIds.length === 0) {
        alert('Please select at least one size');
        return;
      }

      // Check that each selected size has MRP pricing
      const hasAllPricing = selectedSizeIds.every((sizeId) => {
        const pricing = formData.sizesPricingData[sizeId];
        return pricing && parseFloat(pricing.price) > 0;
      });

      if (!hasAllPricing) {
        alert('Please fill in MRP for all selected sizes');
        return;
      }
    }
    
    // Image is optional: send null when no image is selected
    const imageToSend = typeof formData.image === 'string' && formData.image.trim() !== ''
      ? formData.image
      : null;
    
    console.log('Submitting product with image type:', typeof imageToSend, imageToSend ? 'length: ' + imageToSend.length : 'no image');
    
    const payload = {
      name: formData.name,
      description: formData.description,
      price: usesPerSizePricing ? 0 : parseFloat(formData.price), // 0 for per-size pricing categories
      discount: usesPerSizePricing ? 0 : (parseFloat(formData.discount) || 0),
      purchasePrice: usesPerSizePricing ? 0 : (parseFloat(formData.purchasePrice) || 0),
      category: formData.category,
      image: imageToSend,
      sizesData: formData.sizesData,
      sizesPricingData: usesPerSizePricing ? formData.sizesPricingData : {}, // Only use for per-size pricing categories
      offers: formData.offers || [],
      gst: parseInt(formData.gst),
      lowStockThreshold: parseInt(formData.lowStockThreshold) || 10,
      hasRightLeft: formData.hasRightLeft,
      hasNoSizeSelection: formData.hasNoSizeSelection,
      hsn: formData.hsn || '',
      usesPerSizePricing: !formData.hasNoSizeSelection && formData.usesPerSizePricing && !categoryUsesPerSizePricing,
      sizesData: formData.hasNoSizeSelection ? {} : formData.sizesData,
      sizesPricingData: formData.hasNoSizeSelection ? {} : formData.sizesPricingData,
    };
    console.log('AddProductModal submitting payload:', payload);
    onAdd(payload);
    

    setFormData({
      name: '',
      description: '',
      price: '',
      discount: '',
      sellingPrice: '',
      purchasePrice: '',
      category: categories && categories.length > 0 ? categories[0].name : '',
      image: null,
      imagePreview: null,
      sizesData: {},
      sizesPricingData: {},
      offers: [],
      gst: '5',
      lowStockThreshold: '10',
      hasRightLeft: false,
      hasNoSizeSelection: false,
      hsn: '',
      usesPerSizePricing: false,
    });
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: spacing.lg,
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows['2xl'],
          padding: spacing.xl,
          width: '100%',
          maxWidth: '48rem',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
          <h2 style={{ fontSize: '1.875rem', fontWeight: 900, color: colors.text.primary, margin: 0 }}>
            ➕ Add New Product
          </h2>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: colors.text.secondary,
              padding: spacing.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </motion.button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: spacing.lg, marginBottom: spacing.lg }}>
            {/* Product Name */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                Product Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Surgical Gloves"
                required
                style={{
                  width: '100%',
                  padding: `${spacing.sm} ${spacing.md}`,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  e.target.style.borderColor = colors.primary[600];
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = colors.border;
                }}
              />
            </div>

            {/* Category */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: `${spacing.sm} ${spacing.md}`,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  outline: 'none',
                  cursor: 'pointer',
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  e.target.style.borderColor = colors.primary[600];
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = colors.border;
                }}
              >
                <option value="">Select a category</option>
                {categories && categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Product-level per-size pricing toggle - hidden when category already uses it */}
            {(() => {
              const selectedCategory = categories.find(cat => cat.name === formData.category);
              const categoryUsesPerSizePricing = selectedCategory?.usesPerSizePricing === true || formData.category?.toLowerCase() === 'tapes';
              return !categoryUsesPerSizePricing;
            })() && (
              <div style={{ gridColumn: '1 / -1', marginBottom: spacing.lg, padding: spacing.md, backgroundColor: '#f0f4ff', borderRadius: '0.5rem', border: `2px solid ${colors.primary[300]}` }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: spacing.md, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="usesPerSizePricing"
                    checked={formData.usesPerSizePricing}
                    onChange={handleChange}
                    style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer', accentColor: colors.primary[600] }}
                  />
                  <div>
                    <span style={{ fontSize: '0.95rem', fontWeight: 700, color: colors.text.primary }}>
                      💰 This product uses per-size pricing
                    </span>
                    <p style={{ fontSize: '0.75rem', color: colors.text.secondary, margin: `${spacing.xs} 0 0 0` }}>
                      When enabled, each selected size will have its own MRP, cost, discount, and selling price
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Show pricing fields only for non-per-size-pricing products/categories */}
            {(() => {
              const selectedCategory = categories.find(cat => cat.name === formData.category);
              const usesPerSizePricing = selectedCategory?.usesPerSizePricing === true || formData.category?.toLowerCase() === 'tapes' || formData.usesPerSizePricing;
              return !usesPerSizePricing;
            })() && (
              <>
                {/* Price */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                    Product Price (MRP) in ₹ *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    required
                    style={{
                      width: '100%',
                      padding: `${spacing.sm} ${spacing.md}`,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '0.5rem',
                      fontSize: '0.95rem',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                      e.target.style.borderColor = colors.primary[600];
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = colors.border;
                    }}
                  />
                </div>

                {/* Purchase Price */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                Purchase Price (Cost) in ₹ (Optional)
              </label>
              <input
                type="number"
                name="purchasePrice"
                value={formData.purchasePrice}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                    style={{
                      width: '100%',
                      padding: `${spacing.sm} ${spacing.md}`,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '0.5rem',
                      fontSize: '0.95rem',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                      e.target.style.borderColor = colors.primary[600];
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = colors.border;
                    }}
                  />
                </div>

                {/* Discount */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                    Discount (%) *
                  </label>
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleChange}
                    placeholder="0"
                    step="0.01"
                    min="0"
                    max="100"
                    required
                    style={{
                      width: '100%',
                      padding: `${spacing.sm} ${spacing.md}`,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '0.5rem',
                      fontSize: '0.95rem',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                      e.target.style.borderColor = colors.primary[600];
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = colors.border;
                    }}
                  />
                </div>

                {/* Selling Price */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                    Selling Price in ₹
                  </label>
                  <input
                    type="number"
                    name="sellingPrice"
                    value={formData.sellingPrice}
                    onChange={handleChange}
                    placeholder="Auto-calculated or enter manually"
                    step="0.01"
                    min="0"
                    style={{
                      width: '100%',
                      padding: `${spacing.sm} ${spacing.md}`,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '0.5rem',
                      fontSize: '0.95rem',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                      e.target.style.borderColor = colors.primary[600];
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = colors.border;
                    }}
                  />
                  {formData.sellingPrice && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{ marginTop: spacing.md, fontSize: '0.875rem', color: colors.success, margin: 0 }}
                    >
                      💰 Final Price: <strong>₹{Math.round(parseFloat(formData.sellingPrice))}</strong>
                    </motion.p>
                  )}
                </div>
              </>
            )}

            {/* GST */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                HSN Code
              </label>
              <input
                type="text"
                name="hsn"
                value={formData.hsn}
                onChange={handleChange}
                placeholder="e.g., 9018"
                style={{
                  width: '100%',
                  padding: `${spacing.sm} ${spacing.md}`,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  e.target.style.borderColor = colors.primary[600];
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = colors.border;
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                GST (%) *
              </label>
              <select
                name="gst"
                value={formData.gst}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: `${spacing.sm} ${spacing.md}`,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  outline: 'none',
                  cursor: 'pointer',
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  e.target.style.borderColor = colors.primary[600];
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = colors.border;
                }}
              >
                <option value="5">5% GST</option>
                <option value="18">18% GST</option>
              </select>
            </div>

            {/* Low Stock Threshold */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
                Low Stock Alert (units) *
              </label>
              <input
                type="number"
                name="lowStockThreshold"
                value={formData.lowStockThreshold}
                onChange={handleChange}
                placeholder="e.g., 10"
                min="1"
                required
                style={{
                  width: '100%',
                  padding: `${spacing.sm} ${spacing.md}`,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  e.target.style.borderColor = colors.primary[600];
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = colors.border;
                }}
              />
            </div>

          </div>

          {/* Has Right/Left Toggle - Separate from the grid to ensure visibility */}
          <div style={{ marginBottom: spacing.lg, padding: spacing.md, borderRadius: '0.75rem', border: `2px solid ${formData.hasRightLeft ? colors.primary[400] : colors.border}`, backgroundColor: formData.hasRightLeft ? colors.primary[50] : 'white' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: spacing.md, cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="hasRightLeft"
                checked={formData.hasRightLeft}
                onChange={handleChange}
                style={{ width: '1.5rem', height: '1.5rem', cursor: 'pointer', accentColor: colors.primary[600] }}
              />
              <div>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary }}>🔄 Requires Right/Left Selection</span>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: colors.text.secondary }}>
                  Enable this if customers need to choose Right or Left side when buying this product (e.g., for surgical items, braces, etc.)
                </p>
              </div>
            </label>
          </div>

          {/* Has No Size Selection Toggle */}
          <div style={{ marginBottom: spacing.lg, padding: spacing.md, borderRadius: '0.75rem', border: `2px solid ${formData.hasNoSizeSelection ? colors.primary[400] : colors.border}`, backgroundColor: formData.hasNoSizeSelection ? colors.primary[50] : 'white' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: spacing.md, cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="hasNoSizeSelection"
                checked={formData.hasNoSizeSelection}
                onChange={handleChange}
                style={{ width: '1.5rem', height: '1.5rem', cursor: 'pointer', accentColor: colors.primary[600] }}
              />
              <div>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary }}>📦 This product does not consist of size</span>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: colors.text.secondary }}>
                  When enabled, customers will see "Select Quantity" instead of "Select Size" and can buy without choosing a size.
                </p>
              </div>
            </label>
          </div>

          {/* Description */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Product description..."
              rows="3"
              style={{
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                fontSize: '0.95rem',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                e.target.style.borderColor = colors.primary[600];
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = colors.border;
              }}
            />
          </div>

          {/* Sizes Section - Hidden for no-size products */}
          {!formData.hasNoSizeSelection && (
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.md }}>
              Available Sizes
            </label>
            {formData.category && (
              (() => {
                const selectedCategory = categories.find(cat => cat.name === formData.category);
                const selectedSizes = selectedCategory?.sizes || [];
                const sizeNames = selectedSizes.map(s => s.name).join(', ');
                
                return (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      backgroundColor: colors.secondary[50],
                      borderLeft: `4px solid ${colors.secondary[400]}`,
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      color: colors.secondary[700],
                    }}
                  >
                    {selectedSizes.length > 0 ? (
                      <>ℹ️ <strong>{formData.category}:</strong> Sizes automatically selected: <strong>{sizeNames}</strong></>
                    ) : (
                      <>ℹ️ <strong>{formData.category}:</strong> No default sizes set. Add sizes in Categories section.</>
                    )}
                  </motion.div>
                );
              })()
            )}
            <div style={{ border: `1px solid ${colors.border}`, borderRadius: '0.5rem', padding: spacing.lg, backgroundColor: colors.primary[50] }}>
              {(() => {
                const selectedCategory = categories.find(cat => cat.name === formData.category);
                const categorySizeIds = (selectedCategory?.sizes || []).map(s => s.id);
                const displaySizes = sizes.filter(size => categorySizeIds.length === 0 || categorySizeIds.includes(size.id));
                
                if (displaySizes && displaySizes.length > 0) {
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.lg }}>
                      {sortSizes(displaySizes).map((size) => (
                        <label
                          key={size.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                            cursor: 'pointer',
                            padding: spacing.sm,
                            borderRadius: '0.375rem',
                            transition: 'background-color 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colors.primary[100];
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <input
                            type="checkbox"
                            id={`size-${size.id}`}
                            checked={!!formData.sizesData[size.id]}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleSizeQuantityChange(size.id, 1);
                              } else {
                                const newSizesData = { ...formData.sizesData };
                                delete newSizesData[size.id];
                                setFormData({ ...formData, sizesData: newSizesData });
                              }
                            }}
                            style={{
                              width: '1.125rem',
                              height: '1.125rem',
                              cursor: 'pointer',
                              accentColor: colors.primary[600],
                            }}
                          />
                          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: colors.text.primary, whiteSpace: 'nowrap' }}>
                            {size.name.charAt(0).toUpperCase() + size.name.slice(1)}
                          </span>
                          {formData.sizesData[size.id] && (
                            <input
                              type="number"
                              min="1"
                              value={formData.sizesData[size.id]}
                              onChange={(e) => handleSizeQuantityChange(size.id, e.target.value)}
                              placeholder="Qty"
                              style={{
                                width: '4rem',
                                padding: `${spacing.xs} ${spacing.sm}`,
                                border: `1px solid ${colors.primary[300]}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                outline: 'none',
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = colors.primary[600];
                                e.target.style.boxShadow = `0 0 0 2px ${colors.primary[100]}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = colors.primary[300];
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                          )}
                        </label>
                      ))}
                    </div>
                  );
                } else {
                  return <p style={{ color: colors.text.secondary, fontSize: '0.875rem', margin: 0 }}>No sizes for this category. Edit the category to add sizes.</p>;
                }
              })()}
            </div>
          </div>
          )}

          {/* Per-Size Pricing Section */}
          {(() => {
            const selectedCategory = categories.find(cat => cat.name === formData.category);
            const usesPerSizePricing = selectedCategory?.usesPerSizePricing === true || formData.category?.toLowerCase() === 'tapes' || formData.usesPerSizePricing;
            return usesPerSizePricing && Object.keys(formData.sizesData).length > 0;
          })() && (
            <div style={{ marginBottom: spacing.lg, padding: spacing.lg, backgroundColor: colors.secondary[50], borderRadius: '0.5rem', border: `2px solid ${colors.secondary[300]}` }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.lg, margin: '0 0 1rem 0' }}>
                💰 Set Pricing for Each Size
              </h3>
              {(() => {
                const selectedCategory = categories.find(cat => cat.name === formData.category);
                const categorySizeIds = (selectedCategory?.sizes || []).map(s => s.id);
                const selectedSizes = sizes.filter(size => categorySizeIds.includes(size.id) && formData.sizesData[size.id]);
                
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: spacing.lg }}>
                    {sortSizes(selectedSizes).map((size) => {
                      const pricing = formData.sizesPricingData[size.id] || { price: '', purchasePrice: '', discount: '' };
                      const mrp = parseFloat(pricing.price) || 0;
                      const discountPercent = pricing.discount !== '' ? parseFloat(pricing.discount) : 0;
                      const sellingPrice = mrp > 0
                        ? Math.round(mrp * (1 - discountPercent / 100))
                        : 0;
                      
                      return (
                        <div key={size.id} style={{ padding: spacing.md, backgroundColor: 'white', borderRadius: '0.5rem', border: `1px solid ${colors.border}` }}>
                          <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: colors.secondary[600], marginBottom: spacing.md, margin: '0 0 0.75rem 0' }}>
                            📏 Size {size.name}
                          </h4>
                          
                          {/* MRP */}
                          <div style={{ marginBottom: spacing.sm }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                              MRP in ₹
                            </label>
                            <input
                              type="number"
                              value={pricing.price || ''}
                              onChange={(e) => handleSizePricingChange(size.id, 'price', e.target.value)}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              style={{
                                width: '100%',
                                padding: `${spacing.xs} ${spacing.sm}`,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                outline: 'none',
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = colors.primary[600];
                                e.target.style.boxShadow = `0 0 0 2px ${colors.primary[100]}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = colors.border;
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                          
                          {/* Purchase Price */}
                          <div style={{ marginBottom: spacing.sm }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                              Cost in ₹ (Optional)
                            </label>
                            <input
                              type="number"
                              value={pricing.purchasePrice || ''}
                              onChange={(e) => handleSizePricingChange(size.id, 'purchasePrice', e.target.value)}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              style={{
                                width: '100%',
                                padding: `${spacing.xs} ${spacing.sm}`,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                outline: 'none',
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = colors.primary[600];
                                e.target.style.boxShadow = `0 0 0 2px ${colors.primary[100]}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = colors.border;
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                          
                          {/* Discount */}
                          <div style={{ marginBottom: spacing.sm }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                              Discount %
                            </label>
                            <input
                              type="number"
                              value={pricing.discount || ''}
                              onChange={(e) => handleSizePricingChange(size.id, 'discount', e.target.value)}
                              placeholder="0"
                              step="0.01"
                              min="0"
                              max="100"
                              style={{
                                width: '100%',
                                padding: `${spacing.xs} ${spacing.sm}`,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                outline: 'none',
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = colors.primary[600];
                                e.target.style.boxShadow = `0 0 0 2px ${colors.primary[100]}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = colors.border;
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                          
                          {/* Selling Price */}
                          <div style={{ marginBottom: spacing.sm }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                              Selling Price in ₹
                            </label>
                            <input
                              type="number"
                              value={pricing.sellingPrice || ''}
                              onChange={(e) => handleSizePricingChange(size.id, 'sellingPrice', e.target.value)}
                              placeholder="Auto-calculated"
                              step="0.01"
                              min="0"
                              style={{
                                width: '100%',
                                padding: `${spacing.xs} ${spacing.sm}`,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                outline: 'none',
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = colors.primary[600];
                                e.target.style.boxShadow = `0 0 0 2px ${colors.primary[100]}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = colors.border;
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                          
                          {/* Selling Price Display */}
                          {sellingPrice > 0 && (
                            <div style={{ padding: spacing.sm, backgroundColor: colors.success[50], borderRadius: '0.375rem', border: `1px solid ${colors.success[200]}` }}>
                              <p style={{ fontSize: '0.75rem', color: colors.success[700], fontWeight: 600, margin: 0 }}>
                                Final: <strong>₹{sellingPrice.toLocaleString('en-IN')}</strong>
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Quantity-Based Offers */}
          <div style={{ marginBottom: spacing.lg, padding: spacing.lg, backgroundColor: colors.primary[50], borderRadius: '0.75rem', border: `2px solid ${colors.primary[200]}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
                🎁 Bulk Offers
              </h3>
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={addOffer}
                style={{
                  backgroundColor: colors.primary[500],
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  padding: `${spacing.xs} ${spacing.md}`,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                + Add Offer
              </motion.button>
            </div>
            <p style={{ fontSize: '0.8rem', color: colors.text.secondary, margin: `0 0 ${spacing.md} 0` }}>
              Set extra discount when customer buys minimum quantity of this product.
            </p>
            
            {(formData.offers || []).length === 0 && (
              <p style={{ fontSize: '0.875rem', color: colors.text.secondary, margin: 0 }}>No offers added.</p>
            )}
            
            {(formData.offers || []).map((offer, index) => {
              const selectedCategory = categories.find(cat => cat.name === formData.category);
              const categoryUsesPerSize = selectedCategory?.usesPerSizePricing === true || formData.category?.toLowerCase() === 'tapes';
              const usesPerSizePricing = categoryUsesPerSize || formData.usesPerSizePricing;
              const referenceMrp = getOfferReferenceMrp(offer);
              const canCalculateRate = referenceMrp > 0;

              return (
                <div key={index} style={{ marginBottom: spacing.md, padding: spacing.md, backgroundColor: 'white', borderRadius: '0.5rem', border: `1px solid ${colors.border}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: spacing.md, alignItems: 'end' }}>
                    {/* Apply To Size */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                        Apply To
                      </label>
                      <select
                        value={offer.sizeId || 'all'}
                        onChange={(e) => handleOfferChange(index, 'sizeId', e.target.value)}
                        style={{
                          width: '100%',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          outline: 'none',
                          backgroundColor: 'white',
                        }}
                      >
                        <option value="all">All Sizes</option>
                        {usesPerSizePricing && (() => {
                          const selectedCategory = categories.find(cat => cat.name === formData.category);
                          const categorySizeIds = (selectedCategory?.sizes || []).map(s => s.id);
                          return sortSizes(sizes.filter(size => categorySizeIds.includes(size.id) && formData.sizesData[size.id]));
                        })().map((size) => (
                          <option key={size.id} value={size.id}>
                            Size {size.name}
                          </option>
                        ))}
                        {!usesPerSizePricing && sortSizes(sizes.filter(size => formData.sizesData[size.id])).map((size) => (
                          <option key={size.id} value={size.id}>
                            Size {size.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                        Min Qty
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={offer.minQty || ''}
                        onChange={(e) => handleOfferChange(index, 'minQty', e.target.value)}
                        placeholder="e.g., 3"
                        style={{
                          width: '100%',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          outline: 'none',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                        Extra Discount %
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={offer.discountPercent || ''}
                        onChange={(e) => handleOfferChange(index, 'discountPercent', e.target.value)}
                        placeholder="e.g., 10"
                        style={{
                          width: '100%',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          outline: 'none',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                        Rate (₹) {canCalculateRate ? '' : '(auto)'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={offer.rate || ''}
                        onChange={(e) => handleOfferChange(index, 'rate', e.target.value)}
                        placeholder={canCalculateRate ? 'Auto' : 'Set MRP first'}
                        disabled={!canCalculateRate}
                        style={{
                          width: '100%',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          outline: 'none',
                          backgroundColor: canCalculateRate ? 'white' : colors.neutral[100],
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary, marginBottom: spacing.xs }}>
                        Label (optional)
                      </label>
                      <input
                        type="text"
                        value={offer.label || ''}
                        onChange={(e) => handleOfferLabelChange(index, e.target.value)}
                        placeholder="e.g., Buy 3 Get Extra 10%"
                        style={{
                          width: '100%',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          outline: 'none',
                        }}
                      />
                    </div>

                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => removeOffer(index)}
                      style={{
                        backgroundColor: colors.error,
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        padding: `${spacing.xs} ${spacing.sm}`,
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        height: 'fit-content',
                      }}
                    >
                      ✕
                    </motion.button>
                  </div>
                  {!canCalculateRate && offer.sizeId && offer.sizeId !== 'all' && (
                    <p style={{ fontSize: '0.75rem', color: colors.error, margin: `${spacing.xs} 0 0 0` }}>
                      Set MRP for the selected size to enable rate entry.
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Image Upload */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.md }}>
              Product Image (Optional)
            </label>
            <motion.div
              whileHover={{ borderColor: colors.primary[400] }}
              style={{
                border: `2px dashed ${colors.border}`,
                borderRadius: '0.75rem',
                padding: spacing.xl,
                textAlign: 'center',
                transition: 'border-color 0.2s ease',
                backgroundColor: colors.primary[50],
              }}
            >
              {formData.imagePreview ? (
                <div style={{ marginBottom: spacing.lg }}>
                  <img src={formData.imagePreview} alt="Preview" style={{ maxHeight: '10rem', marginLeft: 'auto', marginRight: 'auto', borderRadius: '0.5rem' }} />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    type="button"
                    onClick={() => setFormData({ ...formData, image: null, imagePreview: null })}
                    style={{
                      marginTop: spacing.md,
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: colors.error,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    Remove Image
                  </motion.button>
                </div>
              ) : (
                <div>
                  <p style={{ color: colors.text.secondary, marginBottom: spacing.md, fontSize: '0.95rem', margin: 0 }}>
                    📷 Click to upload product image
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ width: '100%' }}
                  />
                </div>
              )}
            </motion.div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: spacing.lg }}>
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                flex: 1,
                backgroundColor: colors.primary[500],
                color: 'white',
                padding: `${spacing.md} ${spacing.lg}`,
                borderRadius: '0.75rem',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[600];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[500];
              }}
            >
              ✓ Add Product
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              style={{
                flex: 1,
                backgroundColor: colors.text.tertiary,
                color: colors.text.primary,
                padding: `${spacing.md} ${spacing.lg}`,
                borderRadius: '0.75rem',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              Cancel
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}


export default AddProductModal;